import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../../services/api'
import toast from 'react-hot-toast'
import {
  Monitor,
  Play,
  AlertCircle,
  Loader2,
  ArrowRight,
  FolderOpen,
  BookOpen,
  Layers,
  LogOut,
  Circle,
  Clock,
  CheckCircle2,
  Wifi,
  AlertTriangle,
  File as FileIcon,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import LoadingSpinner from '../../components/LoadingSpinner'
import { useAuthStore } from '../../store/authStore'
import {
  useWorkspaceFileActions,
  useExtensionAppMap,
  getExt,
} from '../../hooks/useWorkspaceFileActions'

// ─── Local Agent bridge ─────────────────────────────────────────────────────
// The Digital Lab frontend talks to the local Agent on 127.0.0.1:8750 to:
//   1. Discover which workstation it's running on (GET /device-code)
//   2. Request approved application launches (POST /launch)
// The Agent never exposes device secrets through this bridge.

const AGENT_URL = import.meta.env.VITE_AGENT_URL || 'http://127.0.0.1:8750'

async function fetchDeviceCode() {
  const res = await fetch(`${AGENT_URL}/device-code`)
  if (!res.ok) throw new Error('Agent not reachable')
  const data = await res.json()
  return data.device_code
}

async function fetchPairingCode() {
  const res = await fetch(`${AGENT_URL}/pairing-code`)
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Workstation preparation failed. Please try again.')
  return data.pairing_code
}

async function launchViaAgent(applicationId, launchToken) {
  const res = await fetch(`${AGENT_URL}/launch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ application_id: applicationId, launch_token: launchToken }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Launch failed')
  return data
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// How long this session has been running. A student glancing at the rail wants
// "am I still on the clock", not the wall-clock time they signed in at — the
// old "Active · 05:55 AM" read equally as a start time or as now.
function formatElapsed(iso) {
  if (!iso) return null
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (!Number.isFinite(mins) || mins < 0) return null
  if (mins < 1) return 'Active just now'
  if (mins < 60) return `Active ${mins} min`
  const hours = Math.floor(mins / 60)
  const rest = mins % 60
  return rest === 0 ? `Active ${hours} hr` : `Active ${hours} hr ${rest} min`
}

// Relative modified date for a project file.
function formatFileDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000)
  if (d >= startOfToday) return 'Today'
  if (d >= startOfYesterday) return 'Yesterday'
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

// ─── Application grouping (frontend-only, derived from existing metadata) ────
// The application registry stores { id, name, description, icon, version }.
// There is no category column and this task must not add backend requirements,
// so the workflow grouping below is derived deterministically from the
// application name. Anything unrecognised falls into "Essentials", which is
// rendered last and de-emphasised — so a newly registered application always
// appears, it simply isn't promoted above the creative tools.
const APP_GROUPS = [
  { id: 'photography', label: 'Photography', match: ['photoshop', 'lightroom', 'camera raw', 'bridge', 'capture one', 'luminar', 'gimp', 'affinity photo'] },
  { id: 'design', label: 'Design', match: ['illustrator', 'indesign', 'figma', 'coreldraw', 'corel draw', 'canva', 'inkscape', 'affinity designer', 'affinity publisher'] },
  { id: 'video', label: 'Video', match: ['premiere', 'davinci', 'resolve', 'final cut', 'vegas', 'media encoder', 'capcut', 'shotcut', 'filmora', 'avid'] },
  { id: 'motion', label: 'Motion', match: ['after effects', 'blender', 'animate', 'character animator', 'cinema 4d', 'maya', 'moho', 'toon boom'] },
  { id: 'audio', label: 'Audio', match: ['audition', 'audacity', 'reaper', 'fl studio', 'ableton', 'pro tools'] },
  { id: 'capture', label: 'Capture', match: ['obs', 'streamlabs', 'camtasia', 'snagit'] },
]

const ESSENTIALS_GROUP = { id: 'essentials', label: 'Essentials' }

function groupApplications(apps) {
  const buckets = new Map()
  const essentials = []

  for (const app of apps) {
    const name = (app.name || '').toLowerCase()
    const group = APP_GROUPS.find((g) => g.match.some((token) => name.includes(token)))
    if (!group) {
      essentials.push(app)
      continue
    }
    if (!buckets.has(group.id)) buckets.set(group.id, [])
    buckets.get(group.id).push(app)
  }

  const grouped = APP_GROUPS
    .filter((g) => buckets.has(g.id))
    .map((g) => ({ id: g.id, label: g.label, apps: buckets.get(g.id) }))

  if (essentials.length > 0) {
    grouped.push({ ...ESSENTIALS_GROUP, apps: essentials, secondary: true })
  }
  return grouped
}

// Order the discipline groups by the work the student actually has open.
// A Graphic Design project should not have to scroll past Photography to reach
// Illustrator. Group MEMBERSHIP is untouched — only the order — so a newly
// registered application still lands where groupApplications() put it.
// Essentials always stays last.
const DISCIPLINE_HINTS = [
  { id: 'design', match: ['graphic design', 'design', 'branding', 'layout', 'print', 'illustration'] },
  { id: 'photography', match: ['photography', 'photo', 'retouch', 'lightroom', 'camera'] },
  { id: 'video', match: ['video', 'videography', 'film', 'editing', 'post-production', 'cinema'] },
  { id: 'motion', match: ['motion', 'animation', 'animate', 'vfx', '3d'] },
  { id: 'audio', match: ['audio', 'sound', 'podcast', 'music'] },
  { id: 'capture', match: ['streaming', 'broadcast', 'capture', 'screen'] },
]

function orderGroupsForDiscipline(groups, discipline) {
  const text = (discipline || '').toLowerCase()
  if (!text) return groups

  // Longest keyword wins, so "photo editing" beats a bare "photo" elsewhere.
  const ranked = []
  for (const hint of DISCIPLINE_HINTS) {
    const hit = hint.match.find((token) => text.includes(token))
    if (hit) ranked.push({ id: hint.id, weight: hit.length })
  }
  if (ranked.length === 0) return groups
  ranked.sort((a, b) => b.weight - a.weight)
  const priority = ranked.map((r) => r.id)

  const rank = (group) => {
    if (group.secondary) return 900 // Essentials last, always
    const idx = priority.indexOf(group.id)
    return idx === -1 ? 100 : idx
  }
  return [...groups].sort((a, b) => rank(a) - rank(b))
}

// Display-only: drop the vendor prefix so the tool name leads. The full
// registered name is kept as the tile's title attribute.
function displayAppName(name = '') {
  return name.replace(/^(Adobe|Google|Microsoft)\s+/i, '')
}

function statusConfig(status) {
  switch (status) {
    case 'completed':
      return { label: 'Completed', icon: CheckCircle2, color: 'text-green-400' }
    case 'in_progress':
      return { label: 'In Progress', icon: Clock, color: 'text-cinema-glow' }
    default:
      return { label: 'Not Started', icon: Circle, color: 'text-cinema-muted' }
  }
}

// ─── AppIcon (Slice 25) ──────────────────────────────────────────────────────
// Displays the real Windows application icon extracted from the executable by
// the local Agent. Falls back to the generic Layers icon if:
//   - the Agent is unreachable
//   - the icon can't be extracted (no embedded icon)
//   - the application isn't configured locally
//
// The icon is served from the Agent's /application-icon?id=N endpoint, which
// caches the extracted PNG on disk. The <img> onError handler swaps to the
// fallback silently — no error toast, no broken image.
function AppIcon({ appId, appName, size = 16 }) {
  const [iconError, setIconError] = useState(false)

  if (iconError) {
    return (
      <Layers
        size={size}
        className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0"
      />
    )
  }

  const iconUrl = appName
    ? `${AGENT_URL}/application-icon?id=${appId}&name=${encodeURIComponent(appName)}`
    : `${AGENT_URL}/application-icon?id=${appId}`

  return (
    <img
      src={iconUrl}
      onError={() => setIconError(true)}
      alt=""
      width={size}
      height={size}
      className="shrink-0 transition-opacity group-hover:opacity-90"
      style={{ imageRendering: 'auto' }}
    />
  )
}

// ─── Main Digital Lab Experience (SMA Starting Experience — Slices 11–12) ────
//
// Mental model:
//   SMA WORKSTATION (permanent) → DIGITAL LAB → CURRENT STUDENT (temporary)
//
// The workstation has a permanent SMA identity; the student experience changes
// according to whoever is currently using it. The student's identity comes
// from the existing authenticated Digital Lab session — never hard-coded.
//
// Slice 12 adds a lightweight Current Learning Context so the student
// immediately understands what they are currently working on.

export default function DigitalLabExperience() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const [deviceCode, setDeviceCode] = useState(null)
  const [agentReachable, setAgentReachable] = useState(true)
  const [agentRetrying, setAgentRetrying] = useState(false)
  const [launching, setLaunching] = useState(null)
  // Transient flag set when the student just ended their session (Slice 15).
  // Component-lifetime only — not persisted. Cleared on new session start or
  // unmount. Drives the "Session Finished" confirmation state.
  const [sessionJustEnded, setSessionJustEnded] = useState(false)

  // Discover which workstation we're on via the local Agent.
  // Retry for up to 30 seconds before declaring the Agent unreachable.
  // During retries, show a calm loading state — not an error.
  useEffect(() => {
    let mounted = true
    let retryCount = 0
    const MAX_RETRIES = 15 // 15 × 2s = 30s total
    const RETRY_DELAY = 2000

    function attemptFetch() {
      fetchDeviceCode()
        .then((code) => {
          if (mounted) {
            setDeviceCode(code)
            setAgentReachable(true)
            setAgentRetrying(false)
          }
        })
        .catch(() => {
          if (!mounted) return
          retryCount++
          if (retryCount < MAX_RETRIES) {
            // Still within initialization window — keep retrying
            setAgentRetrying(true)
            setTimeout(attemptFetch, RETRY_DELAY)
          } else {
            // Sustained failure — show error
            setAgentReachable(false)
            setAgentRetrying(false)
          }
        })
    }

    attemptFetch()
    return () => { mounted = false }
  }, [])

  // Fetch the aggregated starting experience (student + device + session +
  // continue learning + counts + applications) in a single call.
  const {
    data: homeData,
    isLoading: homeLoading,
    isError: homeError,
    refetch: refetchHome,
  } = useQuery({
    queryKey: ['digital-lab-home', deviceCode],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/home', {
        params: deviceCode ? { device_code: deviceCode } : {},
      })
      return data
    },
    enabled: !!deviceCode,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  // Start session mutation (includes pairing code for physical workstation verification)
  const startSession = useMutation({
    mutationFn: async () => {
      const pairing_code = await fetchPairingCode()
      const { data } = await api.post('/digital-lab/student/start', { device_code: deviceCode, pairing_code })
      return data
    },
    onSuccess: () => {
      toast.success('Digital Lab session started')
      setSessionJustEnded(false)
      queryClient.invalidateQueries({ queryKey: ['digital-lab-home'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to start session')
    },
  })

  // End session mutation
  const endSession = useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/digital-lab/student/end', { device_code: deviceCode })
      return data
    },
    onSuccess: () => {
      toast.success('Session finished')
      setSessionJustEnded(true)
      // Clear all Digital Lab query caches so the previous student's
      // project/workspace/submission data does not leak to the next student.
      queryClient.removeQueries({ queryKey: ['digital-lab-home'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-projects'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-project'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-project-context'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-project-files'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-project-submission'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-submission-files'] })
      queryClient.removeQueries({ queryKey: ['digital-lab-workspace-token'] })
      queryClient.invalidateQueries({ queryKey: ['digital-lab-home'] })
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to end session')
    },
  })

  // Launch application
  const handleLaunch = useCallback(async (applicationId, applicationName) => {
    setLaunching(applicationId)
    try {
      const { data: launchData } = await api.post('/digital-lab/student/launch-request', {
        application_id: applicationId,
        device_code: deviceCode,
      })
      await launchViaAgent(applicationId, launchData.launch_token)
      toast.success(`${applicationName} launched`)
    } catch (error) {
      const message = error.message || error.response?.data?.error || 'Launch failed'
      toast.error(message)
    } finally {
      setLaunching(null)
    }
  }, [deviceCode])

  // ─── Current project's own files ──────────────────────────────────────────
  // Launching an application from the grid opens it EMPTY: the student then has
  // to File > Open and walk to the project folder — exactly the filesystem
  // knowledge the project-centric model exists to hide. Listing the current
  // project's most recent files here, opening natively, collapses that into one
  // click. Everything below reuses what already exists: the projects list for
  // the canonical workspace_identifier, the Agent's live directory listing, and
  // the shared open-file hook (token refresh and retry included). No new
  // endpoint, no file catalogue — the filesystem stays the source of truth.
  const continueProjectId = homeData?.continue_learning?.project_id
  const homeApplications = homeData?.applications
  const sessionStartedAt = homeData?.session?.started_at

  const { data: projectsList } = useQuery({
    queryKey: ['digital-lab-projects'],
    queryFn: async () => {
      const { data } = await api.get('/digital-lab/student/projects')
      return data
    },
    enabled: !!deviceCode && !!continueProjectId,
    staleTime: 60 * 1000,
  })

  // The canonical project path: the identifier alone, relative to the student's
  // already-resolved workspace. Never prefixed with "Projects/".
  const currentProjectPath = (projectsList?.projects || []).find(
    (p) => p.id === continueProjectId
  )?.workspace_identifier || ''

  const { data: tokenData, refetch: refetchToken } = useQuery({
    queryKey: ['digital-lab-workspace-token', deviceCode],
    queryFn: async () => {
      const { data } = await api.post('/digital-lab/student/workspace/token', {
        device_code: deviceCode,
      })
      return data
    },
    enabled: !!deviceCode && !!currentProjectPath,
    refetchInterval: 3 * 60 * 1000,
  })
  const workspaceToken = tokenData?.workspace_token

  const extAppMap = useExtensionAppMap(
    homeApplications ? { applications: homeApplications } : undefined
  )

  const { withWorkspaceTokenRetry, openFile, opening } = useWorkspaceFileActions({
    workspaceToken,
    refetchToken,
    deviceCode,
    extAppMap,
  })

  const { data: projectListing } = useQuery({
    queryKey: ['digital-lab-home-project-files', workspaceToken, currentProjectPath],
    queryFn: async () => {
      const res = await withWorkspaceTokenRetry((token) => {
        const params = new URLSearchParams({ token, path: currentProjectPath })
        return fetch(`${AGENT_URL}/workspace?${params}`)
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Failed to list project files')
      return body
    },
    enabled: !!workspaceToken && !!currentProjectPath,
    // A file saved from Photoshop should be here when the student alt-tabs back.
    refetchOnWindowFocus: true,
    retry: 1,
  })

  // The three most recently touched files in the project, newest first.
  const recentProjectFiles = (projectListing?.entries || [])
    .filter((e) => e.type === 'file')
    .sort((a, b) => (a.modified < b.modified ? 1 : a.modified > b.modified ? -1 : 0))
    .slice(0, 3)

  // "Opens in Photoshop" — resolved from the workstation's own approved
  // application list, so it names whatever is actually installed here and stays
  // application-agnostic. Unmapped types simply omit the hint and open with the
  // Windows default association.
  const appNameById = new Map((homeApplications || []).map((a) => [a.id, a.name]))
  const opensInLabel = (fileName) => {
    const appId = extAppMap[getExt(fileName)]
    const name = appId ? appNameById.get(appId) : null
    if (!name) return null
    // "Opens in Photoshop", not "Opens in Adobe Photoshop 2025" — the student is
    // being told which application will appear, not which build is installed.
    // A trailing release YEAR only; "PluralEyes 4" keeps its 4.
    return `Opens in ${displayAppName(name).replace(/\s+20\d{2}$/, '')}`
  }

  // ─── Storage health ───────────────────────────────────────────────────────
  // The Agent already knows whether this workstation's configured work drive is
  // reachable. Without this, the page would cheerfully launch Photoshop while
  // the drive was missing and let a student save into a fallback location.
  // Needs no workspace token: it exposes no student data, only drive state.
  const { data: storage } = useQuery({
    queryKey: ['digital-lab-storage-status', deviceCode],
    queryFn: async () => {
      const res = await fetch(`${AGENT_URL}/workspace/storage`)
      if (!res.ok) throw new Error('Storage state unavailable')
      return res.json()
    },
    enabled: !!deviceCode,
    refetchInterval: 60 * 1000,
    retry: 1,
  })
  const workDriveDown = storage?.state === 'unavailable'

  // ─── Loading: Agent still initializing (retrying) ──────────────────────────
  if (agentRetrying) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              SMA Workstation
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <Loader2 size={40} className="mx-auto mb-4 text-cinema-gold/60 animate-spin" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            Loading your SMA environment&hellip;
          </h2>
          <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto">
            Please wait while your workstation connects.
          </p>
        </div>
      </div>
    )
  }

  // ─── Failure: Agent not reachable after sustained retry ───────────────────
  if (!agentReachable) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              SMA Workstation
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <AlertCircle size={40} className="mx-auto mb-4 text-cinema-gold/60" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            SMA Computer Agent not detected on this workstation.
          </h2>
          <p className="font-sans text-sm text-cinema-muted max-w-md mx-auto">
            The Digital Lab experience requires the SMA Computer Agent to be running.
            Please ensure you are using a registered Digital Lab workstation.
          </p>
        </div>
      </div>
    )
  }

  // ─── Loading ─────────────────────────────────────────────────────────────
  if (!deviceCode || homeLoading) return <LoadingSpinner label="Loading your Digital Lab" />

  // ─── Failure: backend problem (graceful, no technical details) ───────────
  if (homeError) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              SMA Workstation
            </h1>
          </div>
        </div>
        <div className="cinema-empty">
          <AlertCircle size={40} className="mx-auto mb-4 text-cinema-gold/60" />
          <h2
            className="text-lg sm:text-xl text-cinema-soft mb-2"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
          >
            We couldn't load your workspace right now.
          </h2>
          <p className="font-sans text-sm text-cinema-muted mb-6">Please try again.</p>
          <button
            onClick={() => refetchHome()}
            className="cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px]"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  const student = homeData?.student
  const device = homeData?.device
  const session = homeData?.session
  const hasActiveSession = session && session.status === 'active' && session.is_mine
  const continueLearning = homeData?.continue_learning
  const counts = homeData?.counts || { projects: 0, assignments: 0, applications: 0 }
  const applications = homeData?.applications || []
  const deviceDisabled = device?.status === 'disabled'
  const anotherStudentSession = session && session.status === 'active' && !session.is_mine

  // ─── No active student session: clean start prompt ────────────────────────
  // The workstation is SMA; the student just needs to begin their session.
  // If sessionJustEnded is true, show a confirmation state instead.
  if (!hasActiveSession) {
    return (
      <div className="w-full max-w-2xl mx-auto min-h-[70vh] flex flex-col justify-center">
        {/* SMA workstation identity (permanent) */}
        <div className="cinema-page-header">
          <div>
            <span className="cinema-label block mb-2">SMA Digital Lab</span>
            <h1
              className="text-2xl sm:text-3xl text-white"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              SMA Workstation
            </h1>
            {!sessionJustEnded && student && (
              <p className="font-sans text-sm text-cinema-muted mt-2">
                Welcome, {student.name}
              </p>
            )}
          </div>
        </div>

        {/* Workstation identity — secondary */}
        {device && (
          <div className="flex items-center gap-2 mb-10 text-cinema-muted">
            <Monitor size={14} className="text-cinema-gold/60" />
            <span className="font-sans text-xs uppercase tracking-[0.18em]">
              {device.computer_name}
            </span>
          </div>
        )}

        {/* Session Finished confirmation (Slice 15) */}
        {sessionJustEnded ? (
          <div className="cinema-empty">
            <CheckCircle2 size={40} className="mx-auto mb-4 text-cinema-glow" />
            <h2
              className="text-lg sm:text-xl text-cinema-soft mb-2"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
            >
              Session Finished
            </h2>
            <p className="font-sans text-sm text-cinema-muted mb-2 max-w-md mx-auto">
              Your SMA workstation is ready for the next student.
            </p>
            <p className="font-sans text-xs text-cinema-muted mb-6 max-w-md mx-auto">
              Your work is saved to your account.
            </p>
            <button
              onClick={() => startSession.mutate()}
              disabled={startSession.isPending || device?.presence !== 'online'}
              className="cinema-btn-primary text-xs sm:text-sm px-6 py-3 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {startSession.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Play size={16} />
              )}
              {startSession.isPending ? 'Preparing this workstation…' : 'Start Session'}
            </button>
            <button
              onClick={() => {
                logout()
                navigate('/login')
              }}
              className="cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px] mt-3"
            >
              <LogOut size={16} />
              Sign Out
            </button>
            <p className="font-sans text-xs text-cinema-muted mt-3 max-w-md mx-auto">
              Sign out so the next student can sign in to this workstation.
            </p>
          </div>
        ) : (
        /* Start session */
        <div className="cinema-empty">
          {deviceDisabled ? (
            <>
              <AlertCircle size={40} className="mx-auto mb-4 text-red-400/70" />
              <h2
                className="text-lg sm:text-xl text-cinema-soft mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
              >
                This workstation is disabled.
              </h2>
              <p className="font-sans text-sm text-cinema-muted">
                Contact an administrator.
              </p>
            </>
          ) : anotherStudentSession ? (
            <>
              <AlertCircle size={40} className="mx-auto mb-4 text-cinema-gold/60" />
              <h2
                className="text-lg sm:text-xl text-cinema-soft mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
              >
                This workstation is currently in use.
              </h2>
              <p className="font-sans text-sm text-cinema-muted mb-6 max-w-md mx-auto">
                Another student has an active session here. Starting your session
                will end theirs.
              </p>
              <button
                onClick={() => startSession.mutate()}
                disabled={startSession.isPending || device?.presence !== 'online'}
                className="cinema-btn-primary text-xs sm:text-sm px-6 py-3 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {startSession.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Play size={16} />
                )}
                {startSession.isPending ? 'Preparing this workstation…' : 'Start My Session'}
              </button>
            </>
          ) : (
            <>
              <h2
                className="text-lg sm:text-xl text-cinema-soft mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}
              >
                Start a Digital Lab session to continue.
              </h2>
              <p className="font-sans text-sm text-cinema-muted mb-6 max-w-md mx-auto">
                This is an SMA workstation. Begin your session to access your
                projects, workspace, applications and learning resources.
              </p>
              <button
                onClick={() => startSession.mutate()}
                disabled={startSession.isPending || device?.presence !== 'online'}
                className="cinema-btn-primary text-xs sm:text-sm px-6 py-3 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {startSession.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Play size={16} />
                )}
                {startSession.isPending ? 'Preparing this workstation…' : 'Start Session'}
              </button>
            </>
          )}
        </div>
        )}
      </div>
    )
  }

  // ─── Active session: full SMA Starting Experience ─────────────────────────
  //
  // Desktop composition (Slice 26 — spatial refinement):
  //
  //   ┌──────────────────────────────────────────────────────────────┐
  //   │ Welcome / program                        workstation · time  │  identity band
  //   ├──────────────────────────────────────────────────────────────┤
  //   │ CURRENT WORK — project, assignment, course        [Continue] │  primary action
  //   ├────────────────────────────────────────────┬─────────────────┤
  //   │ CREATE — applications grouped by discipline │ MY WORK         │
  //   │                                             │ LEARN           │
  //   │                                             │ SESSION         │
  //   └────────────────────────────────────────────┴─────────────────┘
  //
  // The applications occupy the main canvas because this is a workstation
  // launcher; navigation and session control sit in a narrow right rail
  // separated by a hairline rather than a card.
  const currentDiscipline = [
    continueLearning?.course_category,
    continueLearning?.course_title,
  ].filter(Boolean).join(' ')
  const appGroups = orderGroupsForDiscipline(
    groupApplications(applications),
    currentDiscipline
  )

  return (
    <div className="w-full max-w-[1800px] mx-auto">
      {/* Identity band — student (temporary) left, workstation (permanent) right */}
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-2">SMA Digital Lab</span>
          <h1
            className="text-2xl sm:text-3xl xl:text-4xl text-white"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
          >
            Welcome, {student?.first_name || 'Student'}
          </h1>
          <p className="font-sans text-sm text-cinema-muted mt-2">
            {student?.program || 'Your learning context'}
          </p>
        </div>
      </div>

      {/* Current Work — the single primary action, given the full width */}
      <section className="mb-12 lg:mb-16">
        <p className="cinema-label text-cinema-glow mb-5 flex items-center gap-1.5">
          <Play size={12} /> Current work
        </p>
        {continueLearning ? (
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-12">
            <div className="min-w-0 lg:max-w-3xl">
              <h2
                className="text-2xl sm:text-3xl xl:text-[2.5rem] leading-tight text-white"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
              >
                {continueLearning.title}
              </h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
                {continueLearning.assignment_title && (
                  <p className="font-sans text-sm text-cinema-soft">
                    <span className="text-cinema-muted">Assignment · </span>
                    {continueLearning.assignment_title}
                  </p>
                )}
                {continueLearning.course_title && (
                  <p className="font-sans text-xs text-cinema-muted">
                    {continueLearning.course_title}
                    {continueLearning.course_category
                      ? ` · ${continueLearning.course_category}`
                      : ''}
                  </p>
                )}
                {(() => {
                  const st = statusConfig(continueLearning.status)
                  const StIcon = st.icon
                  return (
                    <p className={`font-sans text-xs font-medium flex items-center gap-1.5 ${st.color}`}>
                      <StIcon size={12} /> {st.label}
                    </p>
                  )
                })()}
              </div>
            </div>
            {workDriveDown ? (
              <span
                className="shrink-0 inline-flex items-center justify-center gap-2.5 px-6 py-3 min-h-[44px]
                  border border-white/10 bg-white/[0.03] text-cinema-muted font-display font-medium text-xs sm:text-sm
                  tracking-wide cursor-not-allowed"
                title="Your work drive is unavailable"
              >
                Continue
              </span>
            ) : (
              <Link
                to={`/student/digital-lab/projects/${continueLearning.project_id}`}
                className="shrink-0 cinema-btn-primary text-xs sm:text-sm px-6 py-3 min-h-[44px]"
              >
                Continue <ArrowRight size={16} />
              </Link>
            )}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-12">
            <div className="min-w-0 lg:max-w-3xl">
              <h2
                className="text-2xl sm:text-3xl text-white mb-2"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800 }}
              >
                Nothing in progress yet.
              </h2>
              <p className="font-sans text-sm text-cinema-muted">
                Choose something from your learning workspace to get started.
              </p>
            </div>
            <Link
              to="/student/digital-lab/projects"
              className="shrink-0 cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px]"
            >
              Browse Projects <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {/* Work drive unavailable — the Agent is serving a fallback location.
            Saying nothing here would let a student open an application and save
            into a place their earlier work isn't. */}
        {workDriveDown && (
          <div className="mt-7 max-w-3xl border-l-2 border-amber-500/60 bg-amber-500/[0.06] px-5 py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-sans text-sm text-white font-medium">
                  This workstation&apos;s SMA work drive is unavailable
                </p>
                <p className="font-sans text-xs text-cinema-soft mt-1">
                  {storage?.message ||
                    'Your existing work has not been lost, but it cannot be opened right now. Please tell your trainer before starting new work on this computer.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pick up where you left off — the current project's own recent files,
            opening in their native application through the shared hook. */}
        {!workDriveDown && continueLearning && recentProjectFiles.length > 0 && (
          <div className="mt-7 max-w-3xl">
            <span className="cinema-label text-cinema-muted block mb-1">
              Pick up where you left off
            </span>
            <div className="cinema-list">
              {recentProjectFiles.map((entry) => {
                const isOpening = opening === entry.name
                const opensIn = opensInLabel(entry.name)
                return (
                  <div
                    key={entry.name}
                    className="cinema-list-row group px-3 -mx-3 border-l border-transparent
                      hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
                  >
                    <FileIcon
                      size={18}
                      className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => openFile(entry, currentProjectPath)}
                      disabled={isOpening}
                      className="flex-1 min-w-0 text-left disabled:cursor-wait"
                    >
                      <span className="block font-sans text-sm text-white font-medium truncate">
                        {entry.name}
                      </span>
                      <span className="block font-sans text-xs text-cinema-muted mt-0.5 truncate">
                        {formatFileDate(entry.modified)}
                      </span>
                    </button>
                    {opensIn && (
                      <span className="hidden sm:block font-sans text-xs text-cinema-gold/70 shrink-0">
                        {opensIn}
                      </span>
                    )}
                    {isOpening ? (
                      <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                    ) : (
                      <ArrowRight
                        size={14}
                        className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors shrink-0"
                      />
                    )}
                  </div>
                )
              })}
              <Link
                to={`/student/digital-lab/projects/${continueLearning.project_id}`}
                className="cinema-list-row group px-3 -mx-3 border-l border-transparent
                  hover:border-cinema-gold/40 hover:bg-white/[0.025] transition-colors"
              >
                <FolderOpen
                  size={18}
                  className="text-cinema-muted group-hover:text-cinema-gold transition-colors shrink-0"
                />
                <span className="flex-1 min-w-0">
                  <span className="block font-sans text-sm text-cinema-soft font-medium truncate">
                    All project files
                  </span>
                </span>
                <ArrowRight
                  size={14}
                  className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors shrink-0"
                />
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Working canvas — applications left, navigation & session right */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 xl:gap-16 pt-10 border-t border-white/5">
        {/* ── Create — applications, grouped by creative discipline ─────────── */}
        <section className="xl:col-span-8 2xl:col-span-9 min-w-0">
          <div className="flex items-baseline justify-between mb-8">
            <span className="cinema-label">Create</span>
            {currentDiscipline && (
              <span className="font-sans text-xs text-cinema-muted">
                Ordered for {continueLearning?.course_category || continueLearning?.course_title}
              </span>
            )}
          </div>

          {applications.length === 0 ? (
            <p className="font-sans text-sm text-cinema-muted py-6">
              No applications available on this workstation.
            </p>
          ) : (
            <div className="space-y-10">
              {appGroups.map((group) => (
                <div
                  key={group.id}
                  className={`grid grid-cols-1 md:grid-cols-[9rem_1fr] gap-x-8 gap-y-4 ${
                    group.secondary ? 'pt-7 border-t border-white/5' : ''
                  }`}
                >
                  {/* Discipline label column — the visual spine of the launcher */}
                  <div className={group.secondary ? 'md:pt-1.5' : 'md:pt-4'}>
                    <span
                      className={`font-sans text-xs uppercase tracking-[0.2em] font-medium ${
                        group.secondary ? 'text-cinema-muted' : 'text-cinema-gold'
                      }`}
                    >
                      {group.label}
                    </span>
                  </div>

                  {group.secondary ? (
                    /* Essentials — Acrobat, Calculator, Chrome, Notepad, VLC.
                       They aren't why a student is in the lab, so they get one
                       compact row rather than the same weight as Photoshop. */
                    <div className="flex flex-wrap gap-2">
                      {group.apps.map((app) => (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() => handleLaunch(app.id, app.name)}
                          disabled={launching === app.id}
                          title={`Open ${app.name}`}
                          className="group inline-flex items-center gap-2.5 min-h-[44px] py-2 pl-2.5 pr-3.5
                            border border-white/10 hover:border-cinema-gold/40 hover:bg-white/[0.025]
                            transition-colors duration-300 disabled:opacity-50 disabled:cursor-wait"
                        >
                          <AppIcon appId={app.id} appName={app.name} size={20} />
                          <span className="font-sans text-[13px] text-cinema-soft truncate">
                            {displayAppName(app.name)}
                          </span>
                          {launching === app.id && (
                            <Loader2 size={14} className="text-cinema-glow animate-spin shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-x-6 gap-y-1">
                      {group.apps.map((app) => (
                        <button
                          key={app.id}
                          type="button"
                          onClick={() => handleLaunch(app.id, app.name)}
                          disabled={launching === app.id}
                          title={`Open ${app.name}`}
                          className="group flex items-center gap-4 text-left py-3.5 px-3 -mx-3 min-h-[64px]
                            border-l border-transparent hover:border-cinema-gold/40 hover:bg-white/[0.025]
                            transition-colors duration-300 disabled:opacity-50 disabled:cursor-wait"
                        >
                          <AppIcon appId={app.id} appName={app.name} size={32} />
                          <span className="flex-1 min-w-0">
                            {/* Version numbers are support diagnostics — no student
                                picks an application by version — so they are not shown. */}
                            <span className="block font-sans text-sm text-white font-medium truncate">
                              {displayAppName(app.name)}
                            </span>
                          </span>
                          {launching === app.id && (
                            <Loader2 size={16} className="text-cinema-glow animate-spin shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── Right rail — where to go, and how to finish ────────────────────── */}
        <aside className="xl:col-span-4 2xl:col-span-3 min-w-0 xl:border-l xl:border-white/5 xl:pl-10 2xl:pl-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-10">
            {/* Session — leads the rail. A session left unfinished keeps this
                workstation claimed for the next student, so the way out belongs
                where it is read rather than scrolled past. Workstation identity
                and elapsed time live here too: they were previously in the top
                right of the page, the same concept at the opposite corner. */}
            <div className="sm:col-span-2 xl:col-span-1 pb-8 border-b border-white/5">
              <span className="cinema-label block mb-3">This session</span>

              {device && (
                <div className="flex items-center gap-2 mb-2">
                  <Monitor size={14} className="text-cinema-gold/60 shrink-0" />
                  <span className="font-sans text-sm text-white font-medium">This workstation</span>
                  <span className="inline-flex items-center gap-1.5 ml-auto font-sans text-xs text-emerald-400">
                    <Wifi size={12} strokeWidth={2.5} />
                    {formatElapsed(session?.started_at) || `Active · ${formatTime(session?.started_at)}`}
                  </span>
                </div>
              )}

              <p className="font-sans text-xs text-cinema-muted mb-4 max-w-sm">
                Your work is saved on this workstation and stays in your account.
                Finishing keeps it ready for the next student.
              </p>
              <button
                onClick={() => endSession.mutate()}
                disabled={endSession.isPending}
                className="cinema-btn-ghost text-xs sm:text-sm px-6 py-3 min-h-[44px] hover:border-red-500/40 hover:text-red-400"
              >
                {endSession.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <LogOut size={16} />
                )}
                Finish Session
              </button>
            </div>

            {/* My Work — one place work lives. Projects leads; the full file
                view stays reachable as a quiet link rather than a peer, so the
                student is not asked which of two places holds their work. */}
            <div>
              <span className="cinema-label block mb-3">My Work</span>
              <div className="cinema-list">
                <Link to="/student/digital-lab/projects" className="cinema-list-row group">
                  <FolderOpen size={16} className="text-cinema-muted group-hover:text-cinema-gold transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-white font-medium">Projects</p>
                    <p className="font-sans text-xs text-cinema-muted">
                      {counts.projects} {counts.projects === 1 ? 'project' : 'projects'} · all your work lives here
                    </p>
                  </div>
                  <ArrowRight size={14} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
                </Link>
              </div>
              <Link
                to="/student/digital-lab/workspace"
                className="inline-block mt-3 font-sans text-xs text-cinema-muted hover:text-cinema-gold transition-colors"
              >
                Browse all files &amp; SMA resources
              </Link>
            </div>

            {/* Learn — Assignments is already in the portal's left navigation,
                so it is not repeated here. */}
            <div>
              <span className="cinema-label block mb-3">Learn</span>
              <div className="cinema-list">
                <Link to="/student/masterclass-library" className="cinema-list-row group">
                  <BookOpen size={16} className="text-cinema-muted group-hover:text-cinema-gold transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-white font-medium">Resources</p>
                    <p className="font-sans text-xs text-cinema-muted">Masterclass library</p>
                  </div>
                  <ArrowRight size={14} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
                </Link>
                <Link to="/student/digital-lab/resources" className="cinema-list-row group">
                  <Layers size={16} className="text-cinema-muted group-hover:text-cinema-gold transition-colors" />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm text-white font-medium">Learning Resources</p>
                    <p className="font-sans text-xs text-cinema-muted">Watch &amp; practise inside SMA</p>
                  </div>
                  <ArrowRight size={14} className="text-cinema-muted/40 group-hover:text-cinema-gold/60 transition-colors" />
                </Link>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
